<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BillingAPI from 'dashboard/api/billing';

const SUPPORT_WIDGET_BASE_URL = 'https://app.yeszap.com.br';
const SUPPORT_WIDGET_TOKEN = 'YvqmrcLgpxYHmig6CXVENL4n';
const SUPPORT_WIDGET_SCRIPT_ID = 'yeszap-support-widget-sdk';
const SUPPORT_WIDGET_READY_TIMEOUT = 15000;
const YESZAP_GREEN_CLASS =
  '!bg-[#18d24b] hover:enabled:!brightness-110 focus-visible:!brightness-110';

let supportWidgetInitialization;

const { t, locale } = useI18n();
const { accountId, currentAccount, updateAccount } = useAccount();

const activeTab = ref('billing');
const isSavingCompanyDetails = ref(false);
const isOpeningSupportChat = ref(false);
const isLoadingPlans = ref(true);
const checkoutPlanId = ref(null);
const billingConfigured = ref(false);
const availablePlans = ref([]);
const billingUsage = ref({});
const selectedPaymentMethod = ref('pix');
const showPlansModal = ref(false);

const companyDetails = reactive({
  accountName: '',
  taxId: '',
  legalName: '',
  zipCode: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
});

const customAttributes = computed(
  () => currentAccount.value?.custom_attributes || {}
);

const accountIdentifier = computed(() => `#${accountId.value}`);
const taxIdDigits = computed(() =>
  String(companyDetails.taxId || '').replace(/\D/g, '')
);
const personTypeLabel = computed(() => {
  if (taxIdDigits.value.length > 11) {
    return t('BILLING_SETTINGS.ACCOUNT_DATA.COMPANY');
  }

  return t('BILLING_SETTINGS.ACCOUNT_DATA.INDIVIDUAL');
});
const hasBillingDetails = computed(
  () =>
    Boolean(currentAccount.value?.tax_id) &&
    Boolean(customAttributes.value.billing_legal_name)
);
const currentPlanId = computed(() => customAttributes.value.plan_name);
const currentPlan = computed(() =>
  availablePlans.value.find(plan => plan.id === currentPlanId.value)
);
const paidPlans = computed(() =>
  [...availablePlans.value].sort((firstPlan, secondPlan) => {
    return firstPlan.price_cents - secondPlan.price_cents;
  })
);
const lowestPlan = computed(() => paidPlans.value[0]);

const billingTabs = computed(() => [
  {
    id: 'account',
    label: t('BILLING_SETTINGS.NAV.ACCOUNT_DATA'),
    disabled: false,
  },
  {
    id: 'billing',
    label: t('BILLING_SETTINGS.NAV.PLAN_BILLING'),
    disabled: false,
  },
  {
    id: 'preferences',
    label: t('BILLING_SETTINGS.NAV.PREFERENCES'),
    disabled: true,
  },
  {
    id: 'integrations',
    label: t('BILLING_SETTINGS.NAV.INTEGRATIONS'),
    disabled: true,
  },
  {
    id: 'globals',
    label: t('BILLING_SETTINGS.NAV.GLOBAL_VARIABLES'),
    disabled: true,
  },
  {
    id: 'security',
    label: t('BILLING_SETTINGS.NAV.SECURITY'),
    disabled: true,
  },
]);

const subscriptionIsCurrent = computed(() => {
  const expiration = Date.parse(
    customAttributes.value.yeszap_subscription_ends_on
  );
  return !Number.isFinite(expiration) || expiration > Date.now();
});

const hasActiveCardSubscription = computed(
  () =>
    customAttributes.value.yeszap_payment_method === 'card' &&
    Boolean(customAttributes.value.abacate_pay_subscription_id) &&
    customAttributes.value.yeszap_subscription_status === 'active' &&
    subscriptionIsCurrent.value
);

const formatTaxId = value => {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 14);

  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

watch(
  currentAccount,
  account => {
    const attributes = account?.custom_attributes || {};
    Object.assign(companyDetails, {
      accountName: account?.name || '',
      taxId: formatTaxId(account?.tax_id),
      legalName: attributes.billing_legal_name || '',
      zipCode: attributes.billing_zip_code || '',
      street: attributes.billing_street || '',
      number: attributes.billing_number || '',
      complement: attributes.billing_complement || '',
      neighborhood: attributes.billing_neighborhood || '',
      city: attributes.billing_city || '',
      state: attributes.billing_state || '',
    });
  },
  { immediate: true }
);

const planName = computed(
  () =>
    customAttributes.value.yeszap_plan_display_name ||
    currentPlan.value?.name ||
    customAttributes.value.plan_name ||
    t('BILLING_SETTINGS.NOT_DEFINED')
);

const isSuspended = computed(
  () => currentAccount.value?.status === 'suspended'
);

const isTrialExpired = computed(() => {
  if (customAttributes.value.yeszap_subscription_status !== 'trial') {
    return false;
  }

  const expiration = Date.parse(
    customAttributes.value.yeszap_subscription_ends_on
  );
  return Number.isFinite(expiration) && expiration <= Date.now();
});

const isPaidSubscriptionExpired = computed(() => {
  if (customAttributes.value.yeszap_payment_provider !== 'abacate_pay') {
    return false;
  }
  if (!availablePlans.value.some(plan => plan.id === currentPlanId.value)) {
    return false;
  }

  const expiration = Date.parse(
    customAttributes.value.yeszap_subscription_ends_on
  );
  return Number.isFinite(expiration) && expiration <= Date.now();
});

const isFinanciallyRestricted = computed(
  () =>
    isSuspended.value || isTrialExpired.value || isPaidSubscriptionExpired.value
);

const subscriptionStatus = computed(() => {
  if (isSuspended.value) return 'suspended';
  if (isTrialExpired.value) return 'expired_trial';
  if (isPaidSubscriptionExpired.value) return 'expired_subscription';
  if (!currentPlanId.value && !customAttributes.value.yeszap_payment_provider) {
    return 'trial';
  }

  return (
    customAttributes.value.yeszap_subscription_status ||
    customAttributes.value.subscription_status ||
    'active'
  );
});

const isTrial = computed(() => subscriptionStatus.value === 'trial');

const statusLabels = computed(() => ({
  active: t('BILLING_SETTINGS.STATUS.ACTIVE'),
  trial: t('BILLING_SETTINGS.STATUS.TRIAL'),
  past_due: t('BILLING_SETTINGS.STATUS.PAST_DUE'),
  canceled: t('BILLING_SETTINGS.STATUS.CANCELED'),
  suspended: t('BILLING_SETTINGS.STATUS.SUSPENDED'),
  expired_trial: t('BILLING_SETTINGS.STATUS.EXPIRED_TRIAL'),
  expired_subscription: t('BILLING_SETTINGS.STATUS.EXPIRED_SUBSCRIPTION'),
}));

const statusLabel = computed(
  () =>
    statusLabels.value[subscriptionStatus.value] ||
    t('BILLING_SETTINGS.STATUS.UNKNOWN')
);

const statusClasses = computed(() => {
  if (subscriptionStatus.value === 'active') {
    return 'bg-n-teal-3 text-n-teal-11';
  }
  if (subscriptionStatus.value === 'trial') {
    return 'bg-n-amber-3 text-n-amber-11';
  }

  return 'bg-n-ruby-3 text-n-ruby-11';
});

const billingCycleLabels = computed(() => ({
  monthly: t('BILLING_SETTINGS.CYCLE.MONTHLY'),
  quarterly: t('BILLING_SETTINGS.CYCLE.QUARTERLY'),
  yearly: t('BILLING_SETTINGS.CYCLE.YEARLY'),
}));

const billingCycle = computed(() => {
  const cycle = customAttributes.value.yeszap_billing_cycle;
  return billingCycleLabels.value[cycle] || t('BILLING_SETTINGS.NOT_DEFINED');
});

const subscriptionEndsOnDate = computed(() => {
  const value =
    customAttributes.value.yeszap_subscription_ends_on ||
    customAttributes.value.subscription_ends_on;
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date;
});

const inferredTrialEndsOnDate = computed(() => {
  if (subscriptionEndsOnDate.value) return subscriptionEndsOnDate.value;
  if (!isTrial.value || !currentAccount.value?.created_at) return null;

  const createdAt = new Date(currentAccount.value.created_at);
  if (Number.isNaN(createdAt.getTime())) return null;

  const trialEndsOn = new Date(createdAt);
  trialEndsOn.setDate(trialEndsOn.getDate() + 15);
  return trialEndsOn;
});

const subscriptionEndsOn = computed(() => {
  if (!inferredTrialEndsOnDate.value) return t('BILLING_SETTINGS.NOT_DEFINED');

  const currentLocale = locale.value?.replace('_', '-') || 'pt-BR';
  return new Intl.DateTimeFormat(currentLocale, {
    dateStyle: 'long',
  }).format(inferredTrialEndsOnDate.value);
});

const remainingDays = computed(() => {
  if (!inferredTrialEndsOnDate.value) return null;

  const remainingTime = inferredTrialEndsOnDate.value.getTime() - Date.now();
  return Math.max(0, Math.ceil(remainingTime / 86400000));
});

const formatPrice = priceCents =>
  new Intl.NumberFormat(locale.value?.replace('_', '-') || 'pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(priceCents / 100);

const planHeadline = computed(() => {
  if (isTrial.value) return t('BILLING_SETTINGS.OVERVIEW.TRIAL_TITLE');
  return planName.value;
});

const planSubheadline = computed(() => {
  if (!lowestPlan.value) return t('BILLING_SETTINGS.NOT_DEFINED');

  return t('BILLING_SETTINGS.OVERVIEW.STARTING_AT', {
    price: formatPrice(lowestPlan.value.price_cents),
  });
});

const planTimelineTitle = computed(() => {
  if (isTrial.value) return t('BILLING_SETTINGS.OVERVIEW.TRIAL_ENDS_IN');
  return t('BILLING_SETTINGS.OVERVIEW.NEXT_RENEWAL');
});

const planTimelineValue = computed(() => {
  if (isTrial.value && remainingDays.value !== null) {
    return t('BILLING_SETTINGS.OVERVIEW.DAYS', {
      count: remainingDays.value,
    });
  }

  return subscriptionEndsOn.value;
});

const restrictionTitle = computed(() => {
  if (isTrialExpired.value) return t('BILLING_SETTINGS.EXPIRED_TRIAL.TITLE');
  if (isPaidSubscriptionExpired.value) {
    return t('BILLING_SETTINGS.EXPIRED_SUBSCRIPTION.TITLE');
  }
  return t('BILLING_SETTINGS.SUSPENDED.TITLE');
});

const restrictionDescription = computed(() => {
  if (isTrialExpired.value) {
    return t('BILLING_SETTINGS.EXPIRED_TRIAL.DESCRIPTION');
  }
  if (isPaidSubscriptionExpired.value) {
    return t('BILLING_SETTINGS.EXPIRED_SUBSCRIPTION.DESCRIPTION');
  }
  return t('BILLING_SETTINGS.SUSPENDED.DESCRIPTION');
});

const formatLimit = limit => {
  if (limit === null || limit === undefined) {
    return t('BILLING_SETTINGS.USAGE.UNLIMITED');
  }

  return new Intl.NumberFormat(locale.value?.replace('_', '-') || 'pt-BR', {
    maximumFractionDigits: 0,
  }).format(limit);
};

const normalizeUsage = (usageKey, limitFallback) => {
  const item = billingUsage.value?.[usageKey] || {};
  const used = Number(item.used || 0);
  const limit = item.limit ?? limitFallback;

  return {
    used,
    limit: limit === undefined ? null : limit,
    included: limit === null || Number(limit) > 0,
    percentage:
      Number(limit) > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0,
  };
};

const availableSlots = usage => {
  if (usage.limit === null || usage.limit === undefined) return null;

  return Math.max(0, Number(usage.limit || 0) - usage.used);
};

const usageAvailabilityLabel = (usage, labelBuilder) => {
  const available = availableSlots(usage);
  if (available === null) {
    return t('BILLING_SETTINGS.USAGE.UNLIMITED_AVAILABLE');
  }

  return labelBuilder(available);
};

const usageCounter = usage => {
  return t('BILLING_SETTINGS.USAGE.COUNTER', {
    used: usage.used,
    limit: formatLimit(usage.limit),
  });
};

const usageItems = computed(() => {
  const inboxesUsage = normalizeUsage('inboxes');
  const usersUsage = normalizeUsage('users');
  const whatsappUsage = normalizeUsage(
    'whatsapp_inboxes',
    customAttributes.value.yeszap_whatsapp_inboxes_limit
  );
  const aiAgentsUsage = normalizeUsage(
    'ai_agents',
    customAttributes.value.yeszap_ai_agents_limit
  );

  return [
    {
      ...inboxesUsage,
      key: 'inboxes',
      title: t('BILLING_SETTINGS.USAGE.CHANNELS'),
      description: usageAvailabilityLabel(inboxesUsage, count =>
        t('BILLING_SETTINGS.USAGE.CHANNELS_AVAILABLE', { count })
      ),
    },
    {
      ...usersUsage,
      key: 'users',
      title: t('BILLING_SETTINGS.USAGE.USERS'),
      description: usageAvailabilityLabel(usersUsage, count =>
        t('BILLING_SETTINGS.USAGE.USERS_AVAILABLE', { count })
      ),
    },
    {
      ...whatsappUsage,
      key: 'whatsapp_inboxes',
      title: t('BILLING_SETTINGS.USAGE.WHATSAPP'),
      description: usageAvailabilityLabel(whatsappUsage, count =>
        t('BILLING_SETTINGS.USAGE.WHATSAPP_AVAILABLE', { count })
      ),
    },
    {
      ...aiAgentsUsage,
      key: 'ai_agents',
      title: t('BILLING_SETTINGS.USAGE.AI_AGENTS'),
      description: usageAvailabilityLabel(aiAgentsUsage, count =>
        t('BILLING_SETTINGS.USAGE.AI_AGENTS_AVAILABLE', { count })
      ),
    },
  ];
});

const progressWidthClass = percentage => {
  if (percentage <= 0) return 'w-0';
  if (percentage <= 10) return 'w-1/12';
  if (percentage <= 20) return 'w-1/5';
  if (percentage <= 25) return 'w-1/4';
  if (percentage <= 33) return 'w-1/3';
  if (percentage <= 50) return 'w-1/2';
  if (percentage <= 66) return 'w-2/3';
  if (percentage <= 75) return 'w-3/4';
  if (percentage <= 90) return 'w-11/12';
  return 'w-full';
};

const activeModules = computed(() => {
  const modules = billingUsage.value?.active_modules;
  if (Array.isArray(modules) && modules.length) return modules;
  if (currentPlan.value?.features?.length) return currentPlan.value.features;

  return [];
});

const planLimits = plan => [
  t('BILLING_SETTINGS.PLANS.AGENTS', { count: plan.agents }),
  t('BILLING_SETTINGS.PLANS.CHANNELS', { count: plan.inboxes }),
  t('BILLING_SETTINGS.PLANS.WHATSAPP', {
    count: plan.whatsapp_inboxes,
  }),
  ...(plan.ai_agents
    ? [t('BILLING_SETTINGS.PLANS.AI_AGENTS', { count: plan.ai_agents })]
    : []),
];

const planFeaturePreview = plan => plan.features?.slice(0, 4) || [];

const checkoutDisabled = plan =>
  !plan ||
  !billingConfigured.value ||
  !hasBillingDetails.value ||
  hasActiveCardSubscription.value ||
  (selectedPaymentMethod.value === 'card' && currentPlanId.value === plan.id);

const checkoutLabel = plan => {
  if (currentPlanId.value === plan?.id) {
    return selectedPaymentMethod.value === 'card'
      ? t('BILLING_SETTINGS.PLANS.CURRENT')
      : t('BILLING_SETTINGS.PLANS.RENEW');
  }
  return t('BILLING_SETTINGS.PLANS.SUBSCRIBE');
};

const loadPlans = async () => {
  isLoadingPlans.value = true;
  try {
    const { data } = await BillingAPI.get();
    billingConfigured.value = data.configured;
    availablePlans.value = data.plans || [];
    billingUsage.value = data.usage || {};
  } catch {
    useAlert(t('BILLING_SETTINGS.PLANS.LOAD_ERROR'));
  } finally {
    isLoadingPlans.value = false;
  }
};

const openPlansModal = async () => {
  if (!hasBillingDetails.value) {
    activeTab.value = 'account';
    useAlert(t('BILLING_SETTINGS.PLANS.COMPLETE_DETAILS'));
    return;
  }

  showPlansModal.value = true;
  if (!availablePlans.value.length) await loadPlans();
};

const closePlansModal = () => {
  if (checkoutPlanId.value) return;
  showPlansModal.value = false;
};

const createCheckout = async plan => {
  if (!plan) return;

  if (!hasBillingDetails.value) {
    activeTab.value = 'account';
    useAlert(t('BILLING_SETTINGS.PLANS.COMPLETE_DETAILS'));
    return;
  }

  checkoutPlanId.value = plan.id;
  try {
    const { data } = await BillingAPI.createCheckout({
      planName: plan.id,
      paymentMethod: selectedPaymentMethod.value,
    });
    window.location.assign(data.checkout_url);
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('BILLING_SETTINGS.PLANS.CHECKOUT_ERROR')
    );
  } finally {
    checkoutPlanId.value = null;
  }
};

const waitForSupportWidget = () => {
  if (window.$chatwoot?.hasLoaded) return Promise.resolve();

  return new Promise((resolve, reject) => {
    let timeoutId;
    let pollingId;
    const handleReady = () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(pollingId);
      window.removeEventListener('chatwoot:ready', handleReady);
      resolve();
    };

    pollingId = window.setInterval(() => {
      if (window.$chatwoot?.hasLoaded) handleReady();
    }, 100);

    timeoutId = window.setTimeout(() => {
      window.clearInterval(pollingId);
      window.removeEventListener('chatwoot:ready', handleReady);
      reject(new Error('Support widget initialization timed out'));
    }, SUPPORT_WIDGET_READY_TIMEOUT);

    window.addEventListener('chatwoot:ready', handleReady);
  });
};

const initializeSupportWidget = () => {
  if (window.$chatwoot?.hasLoaded) return Promise.resolve();
  if (supportWidgetInitialization) return supportWidgetInitialization;

  supportWidgetInitialization = new Promise((resolve, reject) => {
    const runWidget = () => {
      const widgetReady = waitForSupportWidget();
      window.chatwootSettings = {
        ...window.chatwootSettings,
        hideMessageBubble: true,
        locale: 'pt_BR',
        position: 'right',
      };
      if (window.$chatwoot) {
        window.$chatwoot.reset();
      } else {
        window.chatwootSDK.run({
          websiteToken: SUPPORT_WIDGET_TOKEN,
          baseUrl: SUPPORT_WIDGET_BASE_URL,
        });
      }

      widgetReady.then(resolve).catch(reject);
    };

    if (window.chatwootSDK) {
      runWidget();
      return;
    }

    const existingScript = document.getElementById(SUPPORT_WIDGET_SCRIPT_ID);
    const script = existingScript || document.createElement('script');
    script.addEventListener('load', runWidget, { once: true });
    script.addEventListener(
      'error',
      () => reject(new Error('Support widget SDK could not be loaded')),
      { once: true }
    );

    if (!existingScript) {
      script.id = SUPPORT_WIDGET_SCRIPT_ID;
      script.src = `${SUPPORT_WIDGET_BASE_URL}/packs/js/sdk.js`;
      script.async = true;
      document.head.appendChild(script);
    }
  }).catch(error => {
    supportWidgetInitialization = null;
    throw error;
  });

  return supportWidgetInitialization;
};

const openSupportChat = async () => {
  isOpeningSupportChat.value = true;
  try {
    await initializeSupportWidget();
    window.$chatwoot.setCustomAttributes({
      yeszap_account_id: String(accountId.value),
      yeszap_account_name: currentAccount.value?.name || '',
      yeszap_support_area: 'financeiro',
    });
    window.$chatwoot.toggle('open');
  } catch {
    useAlert(t('BILLING_SETTINGS.SUPPORT.ERROR'));
  } finally {
    isOpeningSupportChat.value = false;
  }
};

const updateCompanyDetails = async () => {
  if (!companyDetails.taxId.trim() || !companyDetails.legalName.trim()) {
    useAlert(t('BILLING_SETTINGS.ACCOUNT_DATA.REQUIRED_FIELDS'));
    return;
  }

  isSavingCompanyDetails.value = true;
  try {
    await updateAccount({
      name: companyDetails.accountName.trim() || currentAccount.value?.name,
      tax_id: companyDetails.taxId,
      billing_legal_name: companyDetails.legalName.trim(),
      billing_zip_code: companyDetails.zipCode.trim(),
      billing_street: companyDetails.street.trim(),
      billing_number: companyDetails.number.trim(),
      billing_complement: companyDetails.complement.trim(),
      billing_neighborhood: companyDetails.neighborhood.trim(),
      billing_city: companyDetails.city.trim(),
      billing_state: companyDetails.state.trim().toUpperCase(),
    });
    useAlert(t('BILLING_SETTINGS.ACCOUNT_DATA.SUCCESS'));
  } catch {
    useAlert(t('BILLING_SETTINGS.ACCOUNT_DATA.ERROR'));
  } finally {
    isSavingCompanyDetails.value = false;
  }
};

onMounted(loadPlans);
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-5 pb-8">
        <nav
          class="flex gap-6 overflow-x-auto border-y border-n-weak bg-n-background px-1"
          :aria-label="$t('BILLING_SETTINGS.NAV.LABEL')"
        >
          <button
            v-for="tab in billingTabs"
            :key="tab.id"
            type="button"
            class="min-h-12 shrink-0 border-b-2 px-0 text-sm transition-colors"
            :class="
              activeTab === tab.id
                ? 'border-[#18d24b] font-medium text-n-slate-12'
                : tab.disabled
                  ? 'cursor-not-allowed border-transparent text-n-slate-9'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            :disabled="tab.disabled"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </nav>

        <section
          v-if="isFinanciallyRestricted"
          class="flex items-start gap-3 rounded-xl border border-n-ruby-7 bg-n-ruby-3 px-4 py-3"
        >
          <span
            class="i-lucide-circle-alert mt-0.5 size-5 shrink-0 text-n-ruby-11"
          />
          <div>
            <h2 class="text-sm font-medium text-n-ruby-12">
              {{ restrictionTitle }}
            </h2>
            <p class="mt-1 text-sm text-n-ruby-11">
              {{ restrictionDescription }}
            </p>
          </div>
        </section>

        <div v-if="activeTab === 'billing'" class="max-w-5xl">
          <section
            class="rounded-2xl border border-n-weak bg-n-solid-2 px-5 py-5 shadow-sm"
          >
            <div
              class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
            >
              <div>
                <div class="flex flex-wrap items-center gap-2">
                  <h2 class="text-xl font-semibold text-n-slate-12">
                    {{ planHeadline }}
                  </h2>
                  <span
                    class="inline-flex rounded-full px-2.5 py-1 text-xs font-medium"
                    :class="statusClasses"
                  >
                    {{ statusLabel }}
                  </span>
                </div>
                <p class="mt-2 text-sm text-n-slate-11">
                  {{ planSubheadline }}
                </p>
              </div>

              <div class="flex items-center gap-3">
                <button
                  type="button"
                  class="flex size-9 items-center justify-center rounded-full text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :aria-label="$t('BILLING_SETTINGS.OVERVIEW.REFRESH')"
                  @click="loadPlans"
                >
                  <span
                    class="i-lucide-refresh-cw size-4"
                    :class="{ 'animate-spin': isLoadingPlans }"
                  />
                </button>
                <Button
                  color="blue"
                  :label="$t('BILLING_SETTINGS.OVERVIEW.CONTRACT_PLAN')"
                  :class="YESZAP_GREEN_CLASS"
                  :disabled="isLoadingPlans || !paidPlans.length"
                  :is-loading="isLoadingPlans"
                  @click="openPlansModal"
                />
              </div>
            </div>

            <div class="my-5 border-t border-n-weak" />

            <dl class="grid gap-4 sm:grid-cols-3">
              <div>
                <dt class="text-xs text-n-slate-10">
                  {{ planTimelineTitle }}
                </dt>
                <dd class="mt-1 text-sm font-medium text-n-slate-12">
                  {{ planTimelineValue }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-n-slate-10">
                  {{ $t('BILLING_SETTINGS.DETAILS.CYCLE') }}
                </dt>
                <dd class="mt-1 text-sm font-medium text-n-slate-12">
                  {{ billingCycle }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-n-slate-10">
                  {{ $t('BILLING_SETTINGS.DETAILS.ACCOUNT_ID') }}
                </dt>
                <dd class="mt-1 text-sm font-medium text-n-slate-12">
                  {{ accountIdentifier }}
                </dd>
              </div>
            </dl>
          </section>

          <section
            class="mt-6 rounded-2xl border border-n-weak bg-n-solid-2 px-5 py-5 shadow-sm"
          >
            <div class="border-b border-n-weak pb-4">
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ $t('BILLING_SETTINGS.USAGE.TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ $t('BILLING_SETTINGS.USAGE.DESCRIPTION') }}
              </p>
            </div>

            <div class="grid gap-x-8 gap-y-5 py-5 md:grid-cols-2">
              <div v-for="item in usageItems" :key="item.key">
                <div class="flex items-center justify-between gap-3">
                  <span class="text-xs text-n-slate-11">
                    {{ item.title }}
                  </span>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ usageCounter(item) }}
                  </span>
                </div>
                <div
                  class="mt-2 h-1.5 overflow-hidden rounded-full bg-n-slate-5"
                >
                  <div
                    class="h-full rounded-full transition-all"
                    :class="[
                      item.included ? 'bg-[#18d24b]' : 'bg-n-ruby-9',
                      progressWidthClass(item.percentage),
                    ]"
                  />
                </div>
                <p
                  class="mt-2 text-xs"
                  :class="item.included ? 'text-n-slate-10' : 'text-n-ruby-10'"
                >
                  {{
                    item.included
                      ? item.description
                      : $t('BILLING_SETTINGS.USAGE.NOT_INCLUDED')
                  }}
                </p>
              </div>
            </div>

            <div class="border-t border-n-weak pt-4">
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ $t('BILLING_SETTINGS.USAGE.ACTIVE_MODULES') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ $t('BILLING_SETTINGS.USAGE.ACTIVE_MODULES_DESCRIPTION') }}
              </p>
              <div class="mt-3 flex flex-wrap gap-2">
                <span
                  v-for="module in activeModules"
                  :key="module"
                  class="rounded-full bg-n-teal-3 px-3 py-1 text-xs font-medium text-n-teal-11"
                >
                  {{ module }}
                </span>
                <span
                  v-if="!activeModules.length"
                  class="rounded-full bg-n-slate-3 px-3 py-1 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('BILLING_SETTINGS.USAGE.NO_MODULES') }}
                </span>
              </div>
            </div>

            <div
              class="mt-5 flex flex-col gap-4 rounded-xl bg-n-slate-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ $t('BILLING_SETTINGS.SUPPORT_CTA.TITLE') }}
                </h3>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ $t('BILLING_SETTINGS.SUPPORT_CTA.DESCRIPTION') }}
                </p>
              </div>
              <Button
                color="slate"
                icon="i-lucide-message-circle"
                :label="$t('BILLING_SETTINGS.SUPPORT_CTA.BUTTON')"
                :is-loading="isOpeningSupportChat"
                @click="openSupportChat"
              />
            </div>
          </section>
        </div>

        <div v-else-if="activeTab === 'account'" class="max-w-5xl">
          <form
            class="flex flex-col gap-6"
            @submit.prevent="updateCompanyDetails"
          >
            <section
              class="rounded-2xl border border-n-weak bg-n-solid-2 px-5 py-5 shadow-sm"
            >
              <div class="border-b border-n-weak pb-4">
                <h2 class="text-base font-semibold text-n-slate-12">
                  {{ $t('BILLING_SETTINGS.ACCOUNT_DATA.ACCOUNT_NAME_TITLE') }}
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{
                    $t('BILLING_SETTINGS.ACCOUNT_DATA.ACCOUNT_NAME_DESCRIPTION')
                  }}
                </p>
              </div>

              <div class="pt-5">
                <Input
                  v-model="companyDetails.accountName"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.ACCOUNT_NAME')"
                  :placeholder="
                    $t('BILLING_SETTINGS.ACCOUNT_DATA.ACCOUNT_NAME_PLACEHOLDER')
                  "
                  maxlength="255"
                />
              </div>
            </section>

            <section
              class="rounded-2xl border border-n-weak bg-n-solid-2 px-5 py-5 shadow-sm"
            >
              <div class="border-b border-n-weak pb-4">
                <h2 class="text-base font-semibold text-n-slate-12">
                  {{ $t('BILLING_SETTINGS.ACCOUNT_DATA.HOLDER_TITLE') }}
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ $t('BILLING_SETTINGS.ACCOUNT_DATA.HOLDER_DESCRIPTION') }}
                </p>
              </div>

              <div class="grid grid-cols-1 gap-4 pt-5 sm:grid-cols-2">
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('BILLING_SETTINGS.ACCOUNT_DATA.PERSON_TYPE') }}
                  </span>
                  <div
                    class="mt-1 flex h-10 items-center rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12"
                  >
                    {{ personTypeLabel }}
                  </div>
                </div>
                <Input
                  v-model="companyDetails.taxId"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.TAX_ID')"
                  :placeholder="
                    $t('BILLING_SETTINGS.ACCOUNT_DATA.TAX_ID_PLACEHOLDER')
                  "
                  maxlength="18"
                  required
                  @input="
                    companyDetails.taxId = formatTaxId(companyDetails.taxId)
                  "
                />
                <Input
                  v-model="companyDetails.legalName"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.LEGAL_NAME')"
                  :placeholder="
                    $t('BILLING_SETTINGS.ACCOUNT_DATA.LEGAL_NAME_PLACEHOLDER')
                  "
                  class="sm:col-span-2"
                  maxlength="255"
                  required
                />
              </div>
            </section>

            <section
              class="rounded-2xl border border-n-weak bg-n-solid-2 px-5 py-5 shadow-sm"
            >
              <div class="border-b border-n-weak pb-4">
                <h2 class="text-base font-semibold text-n-slate-12">
                  {{ $t('BILLING_SETTINGS.ACCOUNT_DATA.ADDRESS_TITLE') }}
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ $t('BILLING_SETTINGS.ACCOUNT_DATA.ADDRESS_DESCRIPTION') }}
                </p>
              </div>

              <div class="grid grid-cols-1 gap-4 pt-5 sm:grid-cols-2">
                <Input
                  v-model="companyDetails.zipCode"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.ZIP_CODE')"
                  placeholder="00000-000"
                  maxlength="9"
                />
                <Input
                  v-model="companyDetails.street"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.STREET')"
                  :placeholder="
                    $t('BILLING_SETTINGS.ACCOUNT_DATA.STREET_PLACEHOLDER')
                  "
                  maxlength="255"
                />
                <Input
                  v-model="companyDetails.number"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.NUMBER')"
                  maxlength="20"
                />
                <Input
                  v-model="companyDetails.neighborhood"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.NEIGHBORHOOD')"
                  maxlength="100"
                />
                <Input
                  v-model="companyDetails.city"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.CITY')"
                  maxlength="100"
                />
                <Input
                  v-model="companyDetails.state"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.STATE')"
                  :placeholder="
                    $t('BILLING_SETTINGS.ACCOUNT_DATA.STATE_PLACEHOLDER')
                  "
                  maxlength="2"
                />
                <Input
                  v-model="companyDetails.complement"
                  :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.COMPLEMENT')"
                  class="sm:col-span-2"
                  maxlength="100"
                />
              </div>
            </section>

            <div class="flex justify-end">
              <Button
                type="submit"
                color="blue"
                icon="i-lucide-save"
                :is-loading="isSavingCompanyDetails"
                :label="$t('BILLING_SETTINGS.ACCOUNT_DATA.BUTTON')"
                :class="YESZAP_GREEN_CLASS"
              />
            </div>
          </form>
        </div>

        <div
          v-if="showPlansModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-8 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          :aria-label="$t('BILLING_SETTINGS.PLANS.TITLE')"
          @click.self="closePlansModal"
        >
          <section
            class="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-n-weak bg-n-solid-2 shadow-2xl"
          >
            <header
              class="flex flex-col gap-4 border-b border-n-weak px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-8"
            >
              <div>
                <h2 class="text-xl font-semibold text-n-slate-12">
                  {{ $t('BILLING_SETTINGS.PLANS.TITLE') }}
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ $t('BILLING_SETTINGS.PLANS.MODAL_DESCRIPTION') }}
                </p>
              </div>
              <button
                type="button"
                class="flex size-9 shrink-0 items-center justify-center rounded-xl border border-n-weak text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
                :aria-label="$t('BILLING_SETTINGS.PLANS.CLOSE')"
                @click="closePlansModal"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </header>

            <div class="overflow-y-auto px-5 py-5 sm:px-8">
              <div
                class="mx-auto flex w-full max-w-md rounded-2xl border border-n-weak bg-n-background p-1"
                role="group"
                :aria-label="$t('BILLING_SETTINGS.PLANS.PAYMENT_METHOD')"
              >
                <button
                  type="button"
                  class="flex-1 rounded-xl px-4 py-2 text-sm font-medium transition-colors"
                  :class="
                    selectedPaymentMethod === 'pix'
                      ? 'bg-[#18d24b] text-n-slate-12'
                      : 'text-n-slate-10 hover:text-n-slate-12'
                  "
                  @click="selectedPaymentMethod = 'pix'"
                >
                  {{ $t('BILLING_SETTINGS.PLANS.PIX') }}
                </button>
                <button
                  type="button"
                  class="flex-1 rounded-xl px-4 py-2 text-sm font-medium transition-colors"
                  :class="
                    selectedPaymentMethod === 'card'
                      ? 'bg-[#18d24b] text-n-slate-12'
                      : 'text-n-slate-10 hover:text-n-slate-12'
                  "
                  @click="selectedPaymentMethod = 'card'"
                >
                  {{ $t('BILLING_SETTINGS.PLANS.CARD') }}
                </button>
              </div>

              <p
                class="mx-auto mt-3 max-w-2xl text-center text-sm text-n-slate-11"
              >
                {{
                  selectedPaymentMethod === 'pix'
                    ? $t('BILLING_SETTINGS.PLANS.PIX_HELP')
                    : $t('BILLING_SETTINGS.PLANS.CARD_HELP')
                }}
              </p>

              <div
                v-if="isLoadingPlans"
                class="flex min-h-64 items-center justify-center text-n-slate-11"
              >
                <span class="i-lucide-loader-circle mr-2 size-5 animate-spin" />
                {{ $t('BILLING_SETTINGS.PLANS.LOADING') }}
              </div>

              <div
                v-else-if="paidPlans.length"
                class="mt-8 grid gap-5 lg:grid-cols-3"
              >
                <article
                  v-for="(plan, index) in paidPlans"
                  :key="plan.id"
                  class="relative flex min-h-[32rem] flex-col rounded-2xl border px-5 py-6 shadow-sm"
                  :class="
                    index === 1
                      ? 'border-[#18d24b] bg-[#061a10] shadow-2xl shadow-black/30'
                      : 'border-n-weak bg-n-background'
                  "
                >
                  <span
                    v-if="index === 1"
                    class="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-[#18d24b] px-3 py-1 text-xs font-semibold text-n-slate-12"
                  >
                    <span class="i-lucide-star size-3" />
                    {{ $t('BILLING_SETTINGS.PLANS.MOST_POPULAR') }}
                  </span>

                  <div>
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <h3 class="text-lg font-semibold text-n-slate-12">
                          {{ plan.name }}
                        </h3>
                        <p class="mt-2 min-h-10 text-sm text-n-slate-11">
                          {{ plan.description }}
                        </p>
                      </div>
                      <span
                        v-if="currentPlanId === plan.id"
                        class="shrink-0 rounded-full bg-n-teal-3 px-2.5 py-1 text-xs font-medium text-n-teal-11"
                      >
                        {{ $t('BILLING_SETTINGS.PLANS.CURRENT') }}
                      </span>
                    </div>

                    <div class="mt-6">
                      <div class="flex items-end gap-1">
                        <span class="pb-1 text-sm font-medium text-n-slate-11">
                          {{ $t('BILLING_SETTINGS.PLANS.CURRENCY_PREFIX') }}
                        </span>
                        <strong class="text-4xl font-semibold text-n-slate-12">
                          {{
                            formatPrice(plan.price_cents)
                              .replace('R$', '')
                              .trim()
                          }}
                        </strong>
                        <span class="pb-1 text-sm text-n-slate-11">
                          {{ $t('BILLING_SETTINGS.PLANS.PER_MONTH') }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="my-6 border-t border-n-weak" />

                  <dl class="grid gap-3">
                    <div
                      v-for="limit in planLimits(plan)"
                      :key="limit"
                      class="flex items-center justify-between gap-4 text-sm"
                    >
                      <dt class="text-n-slate-11">
                        {{ limit.split(' ').slice(1).join(' ') }}
                      </dt>
                      <dd class="font-semibold text-n-slate-12">
                        {{ limit.split(' ')[0] }}
                      </dd>
                    </div>
                  </dl>

                  <div class="mt-5">
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ $t('BILLING_SETTINGS.PLANS.FEATURES_TITLE') }}
                    </p>
                    <ul class="mt-3 space-y-3">
                      <li
                        v-for="feature in planFeaturePreview(plan)"
                        :key="feature"
                        class="flex gap-2 text-sm text-n-slate-11"
                      >
                        <span
                          class="i-lucide-check mt-0.5 size-4 shrink-0 text-[#18d24b]"
                        />
                        <span>{{ feature }}</span>
                      </li>
                    </ul>
                  </div>

                  <Button
                    class="mt-auto w-full"
                    color="blue"
                    :label="checkoutLabel(plan)"
                    :class="YESZAP_GREEN_CLASS"
                    :disabled="checkoutDisabled(plan)"
                    :is-loading="checkoutPlanId === plan.id"
                    @click="createCheckout(plan)"
                  />
                </article>
              </div>

              <p
                v-else
                class="mt-8 rounded-xl border border-n-weak bg-n-background px-4 py-5 text-center text-sm text-n-slate-11"
              >
                {{ $t('BILLING_SETTINGS.PLANS.NO_PLANS') }}
              </p>

              <div class="mt-6 border-t border-n-weak pt-5">
                <div
                  v-if="!billingConfigured"
                  class="rounded-xl border border-n-ruby-7 bg-n-ruby-3 px-4 py-3 text-sm font-medium text-n-ruby-11"
                >
                  {{ $t('BILLING_SETTINGS.PLANS.NOT_CONFIGURED') }}
                </div>
                <div
                  v-else-if="hasActiveCardSubscription"
                  class="rounded-xl border border-n-amber-7 bg-n-amber-3 px-4 py-3 text-sm font-medium text-n-amber-11"
                >
                  {{ $t('BILLING_SETTINGS.PLANS.ACTIVE_SUBSCRIPTION') }}
                </div>
                <p v-else class="text-center text-xs text-n-slate-10">
                  {{ $t('BILLING_SETTINGS.PLANS.FOOTER_NOTE') }}
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
